import { inject, Injectable, RendererFactory2, DOCUMENT } from '@angular/core';

export type ScriptOptions = {
  id: string;
  targetElement?: HTMLElement;
  type?: string;
  onLoadedListener?: () => void;
  onExistsListener?: () => void;
} & ({ src: string; body?: never } | { src?: never; body: string });

@Injectable({
  providedIn: 'root',
})
export class ScriptService {
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly renderer = this.rendererFactory.createRenderer(null, null);
  private readonly abortControllers = new Map<string, AbortController>();
  private readonly document = inject(DOCUMENT);

  createAndAppend(options: ScriptOptions): HTMLScriptElement | null {
    const script = this.getScriptById(options.id);

    if (script) {
      options.onExistsListener && options.onExistsListener();

      return null;
    }

    const el = this.create(options);
    this.append(el, options);

    return el;
  }

  create(options: ScriptOptions): HTMLScriptElement | null {
    if (this.getScriptById(options.id)) {
      return null;
    }

    const script = this.renderer.createElement('script') as HTMLScriptElement;

    this.renderer.setProperty(script, 'defer', true);
    this.renderer.setProperty(script, 'id', options.id);

    if (options.type) {
      this.renderer.setProperty(script, 'type', options.type);
    }

    if ('src' in options && options.src) {
      this.renderer.setProperty(script, 'src', options.src);
    } else if ('body' in options && options.body) {
      this.renderer.setProperty(script, 'text', options.body);
    }

    if (options.onLoadedListener) {
      const controller = new AbortController();
      this.abortControllers.set(options.id, controller);

      script.addEventListener('load', options.onLoadedListener, {
        passive: true,
        signal: controller.signal,
      });
    }

    return script;
  }

  append(script: HTMLScriptElement | null, options?: ScriptOptions): void {
    if (!script) {
      return;
    }

    if (options) {
      if (this.getScriptById(options.id)) {
        options.onExistsListener && options.onExistsListener();

        return;
      }

      this.renderer.appendChild(options.targetElement ?? this.document.body, script);
    } else {
      this.renderer.appendChild(this.document.body, script);
    }
  }

  remove(id: string): void {
    this.abortControllers.get(id)?.abort();
    const script = this.getScriptById(id);

    if (script) {
      this.renderer.removeChild(script.parentElement, script);
    }
  }

  private getScriptById(id: string): HTMLScriptElement | null {
    return this.document.getElementById(id) as HTMLScriptElement | null;
  }
}
